import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Link,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useRecoilState, useRecoilValue } from 'recoil';
import Delayed from '~/src/components/Delayed';
import LoaderOverlay from '~/src/components/LoaderOverlay';
import { industriesKeys, positionKeys } from '../constants';
import useClearEffect from '../hooks/useClearEffect';
import useSignup from '../hooks/useSignup';
import {
  canSignUpSelector,
  companyNameState,
  confirmPasswordState,
  emailState,
  emailValidSelector,
  errorKeySelector,
  firstNameState,
  industryState,
  isLoadingState,
  lastNameState,
  loginConfigState,
  passwordsMatchSelector,
  passwordState,
  passwordValidSelector,
  positionState,
} from '../store';
import Layout from './NewLayout';

const useStyles = makeStyles(({ palette, spacing }) => ({
  container: {
    overflow: 'hidden',
    '& .signUpTitle': {
      fontWeight: 600,
      fontSize: '22px',
      lineHeight: '1.6',
      margin: '0 0 12px 0',
    },
    '& .signUpButton': {
      background: '#000000',
      color: '#FFFFFF',
      borderColor: '#000000',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '.5rem',
      lineHeight: '1.6',
      padding: '12px 32px',
      borderRadius: '25px',
      fontWeight: 600,
      fontSize: '16px',
      border: '2px solid transparent',
      '&.Mui-disabled': {
        color: '#a6a6a6',
        borderColor: 'rgba(0, 0, 0, 0.12)',
        backgroundColor: 'rgba(0, 0, 0, 0.12)',
      },
    },
  },
  formControlContainer: {
    '& label': {
      padding: 0,
      fontWeight: 600,
      lineHeight: '1.6',
      fontSize: '13px',
      marginBottom: '6px',
      color: 'var(--black)',
    },
    '& .MuiFilledInput-root': {
      background: 'none',
      borderRadius: '0',
    },
    '& input': {
      width: '100%',
      border: '1.5px solid #aba69c',
      borderRadius: '10px !important',
      fontSize: '15px',
    },
  },
  formControlSelectContainer: {
    '& label': {
      padding: 0,
      fontWeight: 600,
      lineHeight: '1.6',
      fontSize: '13px',
      marginBottom: '6px',
      color: 'var(--black)',
    },
    '& .MuiOutlinedInput-root': {
      background: 'none',
      borderRadius: '10px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: '1.5px solid #aba69c',
    },
    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#aba69c',
    },
    '& .MuiSelect-select': {
      fontSize: '15px',
      '&:focus': {
        background: '#FFFFFF',
      },
    },
  },
  formControlCheckboxContainer: {
    marginLeft: 0,
    '& .MuiCheckbox-root': {
      padding: 0,
      marginRight: '10px',
    },
    '& .Mui-checked': {
      color: '#000000',
      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
    },
    '& .MuiTypography-root': {
      fontSize: '15px',
    },
  },
  signInLink: {
    color: palette.secondary.main,
    fontWeight: 600,
    lineHeight: 1.5,
    marginLeft: '5px',
  },
  industryError: {
    color: palette.error.main,
  },
  selectField: {
    '& .MuiSelect-root': {
      padding: `${spacing(2.5)}px ${spacing(3)}px`,
      lineHeight: `${spacing(5)}px`,
    },
  },
}));

const SignUp: React.FC = () => {
  const {
    container,
    formControlContainer,
    formControlSelectContainer,
    formControlCheckboxContainer,
    industryError,
    selectField,
  } = useStyles();
  const { t } = useTranslation('auth');
  const { push } = useHistory();
  const [signup] = useSignup();
  const [user, setUser] = useRecoilState(emailState);
  const [pass, setPass] = useRecoilState(passwordState);
  const [firstName, setFirstName] = useRecoilState(firstNameState);
  const [lastName, setLastName] = useRecoilState(lastNameState);
  const [companyName, setCompanyName] = useRecoilState(companyNameState);
  const [industry, setIndustry] = useRecoilState(industryState);
  const [position, setPosition] = useRecoilState(positionState);
  const [confirmPass, setConfirmPass] = useRecoilState(confirmPasswordState);
  const { allowSignUp } = useRecoilValue(loginConfigState);
  const userValid = useRecoilValue(emailValidSelector);
  const isPasswordValid = useRecoilValue(passwordValidSelector);
  const doPasswordsMatch = useRecoilValue(passwordsMatchSelector);
  const signUpPending = useRecoilValue(isLoadingState);
  const canSignUp = useRecoilValue(canSignUpSelector);
  const errorKey = useRecoilValue(errorKeySelector);

  const [userTouched, setUserTouched] = useState(false);
  const [passTouched, setPassTouched] = useState(false);
  const [firstNameTouched, setFirstNameTouched] = useState(false);
  const [lastNameTouched, setLastNameTouched] = useState(false);
  const [companyNameTouched, setCompanyNameTouched] = useState(false);
  const [industryTouched, setIndustryTouched] = useState(false);
  const [confirmPassTouched, setConfirmPassTouched] = useState(false);
  const [agreement, setAgreement] = useState(false);

  useClearEffect();

  useEffect(() => {
    if (!allowSignUp) {
      push('/login');
    }
  }, [allowSignUp]);

  const handleUserChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserTouched(true);
    setUser(e.target.value);
  };

  const handlePassChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassTouched(true);
    setPass(e.target.value);
  };

  const handleConfirmPassChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassTouched(true);
    setConfirmPass(e.target.value);
  };

  const handleFirstNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFirstNameTouched(true);
    setFirstName(e.target.value);
  };

  const handleLastNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLastNameTouched(true);
    setLastName(e.target.value);
  };

  const handleCompanyNameChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCompanyNameTouched(true);
    setCompanyName(e.target.value);
  };

  const handleIndustryChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIndustry(e.target.value);
  };

  const handleIndustryTouched = () => {
    setIndustryTouched(true);
  };

  const handlePositionChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPosition(e.target.value);
  };

  return (
    <Layout
      chipText={t('signUp.layoutContainer.title')}
      description={t('signUp.layoutContainer.description')}
      cursorAfterText={t('signUp.layoutContainer.cursorAfterText')}
      cursorBeforeText={t('signUp.layoutContainer.cursorBeforeText')}
      buttonArray={[
        { label: t('signUp.layoutContainer.buttonText'), link: '/login' },
      ]}
    >
      <Box className={container}>
        {signUpPending && (
          <Delayed>
            <LoaderOverlay />
          </Delayed>
        )}
        <Box flex="auto" display="flex" flexDirection="column">
          <Typography variant="h3" className="signUpTitle">
            {t('signUp.title')}
          </Typography>
          {!!errorKey && (
            <Box pb={6}>
              <Alert severity="error">{t(errorKey)}</Alert>
            </Box>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('signUp.firstName.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="text"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={firstName}
                  onChange={handleFirstNameChanged}
                  error={firstNameTouched && !firstName}
                  helperText={
                    firstNameTouched
                    && !firstName
                    && t('signUp.firstName.required')
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('signUp.lastName.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="text"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={lastName}
                  onChange={handleLastNameChanged}
                  error={lastNameTouched && !lastName}
                  helperText={
                    lastNameTouched
                    && !lastName
                    && t('signUp.lastName.required')
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('signUp.email.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="email"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={user}
                  onChange={handleUserChanged}
                  error={
                    (userTouched && !user)
                    || (userTouched && !!user && !userValid)
                  }
                  helperText={
                    (userTouched && !user && t('signUp.email.required'))
                    || (userTouched
                      && !!user
                      && !userValid
                      && t('signUp.email.validation'))
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('signUp.companyName.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="text"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={companyName}
                  onChange={handleCompanyNameChanged}
                  error={companyNameTouched && !companyName}
                  helperText={
                    companyNameTouched
                    && !companyName
                    && t('signUp.companyName.required')
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl
                className={formControlSelectContainer}
                fullWidth
                required
              >
                <FormLabel>{t('signUp.industry.label')}</FormLabel>
                <Select
                  className={selectField}
                  variant="outlined"
                  value={industry}
                  onChange={handleIndustryChanged}
                  onClick={handleIndustryTouched}
                  displayEmpty
                >
                  {industriesKeys.map((item) => (
                    <MenuItem key={item} value={item}>
                      {t(`signUp.industry.${item}`)}
                    </MenuItem>
                  ))}
                </Select>
                {industryTouched && !industry && (
                  <FormHelperText className={industryError}>
                    {t('signUp.industry.required')}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlSelectContainer} fullWidth>
                <FormLabel>{t('signUp.position.label')}</FormLabel>
                <Select
                  className={selectField}
                  variant="outlined"
                  value={position}
                  onChange={handlePositionChanged}
                  displayEmpty
                >
                  {positionKeys.map((item) => (
                    <MenuItem key={item} value={item}>
                      {t(`signUp.position.${item}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('signUp.password.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="password"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={pass}
                  onChange={handlePassChanged}
                  error={(passTouched && !pass) || (!!pass && !isPasswordValid)}
                  helperText={
                    (passTouched && !pass && t('signUp.password.required'))
                    || (passTouched
                      && pass
                      && !isPasswordValid
                      && t('signUp.password.validation'))
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl className={formControlContainer} fullWidth required>
                <FormLabel>{t('signUp.confirmPassword.label')}</FormLabel>
                <TextField
                  autoComplete="off"
                  type="password"
                  variant="filled"
                  hiddenLabel
                  InputProps={{ disableUnderline: true }}
                  FormHelperTextProps={{ variant: 'standard' }}
                  value={confirmPass}
                  onChange={handleConfirmPassChanged}
                  error={
                    (confirmPassTouched && !pass)
                    || (confirmPassTouched && !!pass && !doPasswordsMatch)
                  }
                  helperText={
                    (confirmPassTouched
                      && !confirmPass
                      && t('signUp.confirmPassword.required'))
                    || (confirmPassTouched
                      && confirmPass
                      && !doPasswordsMatch
                      && t('signUp.confirmPassword.validation'))
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
          <Box my={3}>
            <FormControlLabel
              className={formControlCheckboxContainer}
              control={
                <Checkbox
                  checked={agreement}
                  onChange={(event) => setAgreement(event.target.checked)}
                  name="checkedB"
                />
              }
              label={
                <>
                  <Typography component="span">
                    {t('signUp.licenseAgreement.label')}
                  </Typography>
                  <Typography>
                    <Link href="https://squarkai.com/EULA/" target="_blanc">
                      {t('signUp.licenseAgreement.link')}
                    </Link>
                  </Typography>
                </>
              }
            />
          </Box>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={!canSignUp || !agreement}
            onClick={signup}
            className="signUpButton"
          >
            {t('signUp.buttons.signUp')}
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};

export default SignUp;
